import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { CardAction } from '@/common/components/CardAction/CardAction';
import { CardActionGroup } from '@/common/components/CardActionGroup/CardActionGroup';
import { CardActionLink } from '@/common/components/CardActionLink/CardActionLink';
import { CardActionToggle } from '@/common/components/CardActionToggle/CardActionToggle';
import { Drawer } from '@/common/components/Drawer/Drawer';
import { IconBadge } from '@/common/components/IconBadge/IconBadge';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { getCountryByCode } from '@/common/constants/country-list';
import { ROUTES } from '@/common/constants/routes';
import { ConfirmCancelCardModal } from '@/common/containers/Dashboard/Profile/CardDetailsPage/components/CardActions/components/ConfirmCancelCardModal/ConfirmCancelCardModal';
import { RequestCardPinModal } from '@/common/containers/Dashboard/Profile/CardDetailsPage/components/CardActions/components/RequestCardPinModal/RequestCardPinModal';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { useFeatures } from '@/common/integrations/feature-flag/feature-flag.hooks';
import {
  useLockCard,
  useUnlockCard,
} from '@/common/integrations/menhir/employee/card/card.hooks';
import { ICard } from '@/common/integrations/menhir/employee/card/card.types';
import { notify } from '@/common/utils/toast/toast';
import { useRouter } from 'expo-router';

export interface IProps {
  card: ICard;
  needsReplacement?: boolean;
  onTogglePIN?: (active: boolean) => void;
}

export const CardActions = ({ card, onTogglePIN }: IProps) => {
  const { t } = useTranslation();
  const router = useRouter();

  const {
    features: { ff_digital_cards, ff_sea_account_card_management_request },
  } = useFeatures();

  const { mutateAsync: lockCard, isLoading: isLocking } = useLockCard();
  const { mutateAsync: unlockCard, isLoading: isUnlocking } = useUnlockCard();

  const { company } = useEmployeeDetails();

  const [displayConfirmCancelModal, toggleConfirmCancelModal] = useState(false);
  const [displayRequestPinModal, toggleRequestPinModal] = useState(false);
  const [didntReceiveCardDrawerOpen, setDidntReceiveCardDrawerOpen] =
    useState(false);

  const { format: cardFormat, status: cardStatus, isExpiring } = card;

  const isDigital = cardFormat === 'digital' && card.status === 'active';

  const handleChangeFreeze = async () => {
    try {
      if (cardStatus === 'locked') {
        await unlockCard({ cardId: card.id });
      } else if (cardStatus === 'active') {
        await lockCard({ cardId: card.id });
      }
    } catch {
      notify({
        type: 'error',
        message:
          cardStatus === 'locked'
            ? t('dashboard.card.actions.unfreeze.error')
            : t('dashboard.card.actions.freeze.error'),
      });
    }
  };

  const requestCard = async () => {
    try {
      if (ff_sea_account_card_management_request) {
        router.push(ROUTES.CARD_NEW_REQUEST);
      } else {
        router.push(ROUTES.CARD_REQUEST);
      }
    } catch (error) {
      notify({
        type: 'error',
        message: t('errors.api.server'),
      });
    }
  };

  const isSubmitting = isLocking || isUnlocking;

  return (
    <>
      <CardActionGroup size="sm">
        {(!isDigital && card.status === 'inactive') ||
        (isDigital && card.isPlasticRequested) ? (
          <CardAction
            title={t('dashboard.card.actions.activate_physical_card.title')}
            icon={
              <IconBadge
                icon={<Icon icon="credit-card" color="neutral10" />}
                ratio={56}
                backgroundColor="accentSecondary40"
              />
            }
            onPress={() => router.push(ROUTES.CARD_ACTIVATION)}
            action={<Icon icon="chevron-right" />}
          />
        ) : null}

        {['active', 'locked'].includes(cardStatus) ? (
          <CardActionToggle
            icon={<Icon icon="shield-keyhole" />}
            title={t(
              `dashboard.card.actions.${
                cardStatus === 'active' ? 'freeze' : 'unfreeze'
              }.title`
            )}
            description={t(
              `dashboard.card.actions.${
                cardStatus === 'active' ? 'freeze' : 'unfreeze'
              }.description`
            )}
            checked={cardStatus === 'locked'}
            onChange={() => {
              if (!isSubmitting) {
                handleChangeFreeze();
              }
            }}
            disabled={isSubmitting}
          />
        ) : null}

        {cardStatus === 'locked' ? (
          <CardAction
            icon={<Icon icon="ban" />}
            title={t('dashboard.card.actions.cancel.title')}
            description={t('dashboard.card.actions.cancel.description')}
            onPress={() => toggleConfirmCancelModal(true)}
            action={<Icon icon="chevron-right" />}
          />
        ) : null}

        {(!ff_digital_cards && cardStatus === 'active') ||
        (ff_digital_cards &&
          cardStatus === 'active' &&
          card.format === 'plastic') ? (
          onTogglePIN ? (
            <CardAction
              icon={<Icon icon="lock" />}
              title={t('dashboard.card.actions.change_pin.title')}
              onPress={() => onTogglePIN(true)}
              action={<Icon icon="chevron-right" />}
            />
          ) : (
            <CardAction
              icon={<Icon icon="lock" />}
              title={t('dashboard.card.actions.request_pin.title')}
              description={t('dashboard.card.actions.request_pin.description')}
              onPress={() => toggleRequestPinModal(true)}
              action={<Icon icon="chevron-right" />}
            />
          )
        ) : null}

        {(!isDigital && card.status === 'inactive') ||
        (isDigital && card.isPlasticRequested) ? (
          <CardAction
            title={t('dashboard.card.actions.didnt_receive_card.title')}
            icon={<Icon icon="message-question" />}
            onPress={() => {
              setDidntReceiveCardDrawerOpen(true);
            }}
            action={<Icon icon="chevron-right" />}
          />
        ) : null}

        <CardActionLink
          title={t('dashboard.card.actions.support.title')}
          icon={<Icon icon="question" />}
          href={t('help_link.card.support')}
          external
        />

        {company.settings.cardRequestEmployeePermission !== 'digital_only' &&
        isDigital &&
        !isExpiring &&
        !card.isPlasticRequested ? (
          <CardAction
            title={t('dashboard.card.actions.request_physical_card.title')}
            icon={<Icon icon="credit-card" />}
            action={<Icon icon="chevron-right" />}
            onPress={requestCard}
          />
        ) : null}
      </CardActionGroup>

      {didntReceiveCardDrawerOpen && (
        <Drawer
          title={t('dashboard.card.actions.didnt_receive_card.title')}
          onClose={() => setDidntReceiveCardDrawerOpen(false)}
        >
          <Stack gap="md">
            <BodyText>
              {t('dashboard.card.actions.didnt_receive_card.drawer.address')}
            </BodyText>
            {card.shippingAddress && (
              <View>
                <BodyText bold>
                  {[
                    card.shippingAddress.addressLine1,
                    card.shippingAddress.addressLine2 || '',
                  ].join(' ')}
                </BodyText>
                <BodyText bold>
                  {[
                    card.shippingAddress.zipCode,
                    card.shippingAddress.city,
                  ].join(' ')}
                </BodyText>
                <BodyText bold>{card.shippingAddress.district || ''}</BodyText>
                <BodyText bold>
                  {getCountryByCode(card.shippingAddress?.country)?.name ?? ''}
                </BodyText>
              </View>
            )}
            <BodyText>
              <Translate i18nKey="dashboard.card.actions.didnt_receive_card.drawer.requested_date" />
            </BodyText>
          </Stack>
        </Drawer>
      )}

      {displayConfirmCancelModal && (
        <ConfirmCancelCardModal
          onClose={() => toggleConfirmCancelModal(false)}
        />
      )}

      {displayRequestPinModal && (
        <RequestCardPinModal
          cardId={card.id}
          onClose={() => toggleRequestPinModal(false)}
        />
      )}
    </>
  );
};
